import type {
  CodeChangeToolCallProgressStep,
  ToolCallProgress,
} from "schemas/generated/chatMessage";

import { exhaustiveSwitch } from "replo-utils/lib/misc";

export interface StepDisplay {
  id: string;
  headline: string;
  subheadline?: string;
  isCompleted: boolean;
}

const getStepDisplayInfo = (opts: {
  step: CodeChangeToolCallProgressStep;
  isCompleted: boolean;
}): StepDisplay => {
  const { step, isCompleted } = opts;
  return exhaustiveSwitch(
    step,
    "stepType",
  )({
    determiningSwarmSections: (step) => {
      return {
        id: step.id,
        headline: isCompleted
          ? "Analyzed page structure"
          : "Analyzing page structure...",
        isCompleted,
      };
    },
    applyingChanges: (step) => {
      return {
        id: step.id,
        headline: isCompleted ? "Generated changes" : "Generating changes...",
        subheadline:
          step.changes && step.changes.lineCount > 0
            ? `Making ${step.changes.lineCount} edits...`
            : "Preparing to make changes...",
        isCompleted,
      };
    },
    applyingSwarmChanges: (step) => {
      return {
        id: step.id,
        headline: isCompleted ? "Generated changes" : "Generating changes...",
        subheadline:
          step.changes && step.changes.lineCount > 0
            ? `Making ${step.changes.lineCount} edits, ${step.changes.agentsCompleted}/${step.changes.totalAgents} sections...`
            : "Preparing to make changes across multiple sections...",
        isCompleted,
      };
    },
    runningFixerAgent: (step) => {
      return {
        id: step.id,
        headline: isCompleted ? "Verified changes" : "Verifying changes...",
        subheadline: step.changes
          ? `Fixing ${step.changes.lineCount} edits...`
          : "Ensuring quality and consistency...",
        isCompleted,
      };
    },
  });
};

export const getStepsFromProgress = (
  progress?: ToolCallProgress,
): StepDisplay[] => {
  if (!progress) {
    return [];
  }

  // Last step is considered active, all previous steps are completed
  return progress.steps.map((step, index) => {
    const isActive = index === progress.steps.length - 1;
    return getStepDisplayInfo({ step, isCompleted: !isActive });
  });
};
